'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import { AlertTriangle, AlertCircle, CheckCircle2 } from 'lucide-react';
import {
  redFlags,
  yellowFlags,
  greenFlags,
  FlagPattern,
} from '@/lib/tool-data/red-flags';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface DetectedFlag {
  flag: FlagPattern;
  matchedText: string;
}

interface AnalysisResult {
  red: DetectedFlag[];
  yellow: DetectedFlag[];
  green: DetectedFlag[];
}

/* ------------------------------------------------------------------ */
/*  Detection logic                                                    */
/* ------------------------------------------------------------------ */

function detectFlags(text: string): AnalysisResult {
  const detect = (patterns: FlagPattern[]): DetectedFlag[] => {
    const found: DetectedFlag[] = [];
    for (const flag of patterns) {
      try {
        const regex = new RegExp(flag.pattern, 'gi');
        const match = regex.exec(text);
        if (match) {
          found.push({ flag, matchedText: match[0] });
        }
      } catch {
        // If regex is invalid, fall back to simple includes
        if (text.toLowerCase().includes(flag.pattern.toLowerCase())) {
          found.push({ flag, matchedText: flag.label });
        }
      }
    }
    return found;
  };

  return {
    red: detect(redFlags),
    yellow: detect(yellowFlags),
    green: detect(greenFlags),
  };
}

/* ------------------------------------------------------------------ */
/*  Flag Card component                                                */
/* ------------------------------------------------------------------ */

function FlagCard({
  detected,
  variant,
}: {
  detected: DetectedFlag;
  variant: 'red' | 'yellow' | 'green';
}) {
  const styles = {
    red: 'bg-red-50 border border-red-200 rounded-xl p-4 mb-3',
    yellow: 'bg-amber-50 border border-amber-200 rounded-xl p-4 mb-3',
    green: 'bg-green-50 border border-green-200 rounded-xl p-4 mb-3',
  };

  const labelStyles = {
    red: 'text-red-800 font-bold text-lg',
    yellow: 'text-amber-800 font-bold text-lg',
    green: 'text-green-800 font-bold text-lg',
  };

  const matchStyles = {
    red: 'text-red-700',
    yellow: 'text-amber-600',
    green: 'text-green-600',
  };

  const headingStyles = {
    red: 'text-red-700',
    yellow: 'text-amber-700',
    green: 'text-green-700',
  };

  return (
    <div className={styles[variant]}>
      <div className="mb-2">
        <span className={labelStyles[variant]}>{detected.flag.label}</span>
        <span className={`ml-2 text-sm italic ${matchStyles[variant]}`}>
          &quot;{detected.matchedText}&quot;
        </span>
      </div>
      <div className="space-y-2 text-sm">
        <div>
          <span className={`font-semibold ${headingStyles[variant]}`}>
            What it means:{' '}
          </span>
          <span className="text-gray-700">{detected.flag.meaning}</span>
        </div>
        <div>
          <span className={`font-semibold ${headingStyles[variant]}`}>
            What to do:{' '}
          </span>
          <span className="text-gray-700">{detected.flag.advice}</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function RedFlagContent() {
  const [jobDescription, setJobDescription] = useState('');
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);

  function handleDetect() {
    if (!jobDescription.trim()) return;
    const analysis = detectFlags(jobDescription);
    setResult(analysis);
    setHasAnalyzed(true);
  }

  function handleReset() {
    setJobDescription('');
    setResult(null);
    setHasAnalyzed(false);
  }

  const totalFlags = result
    ? result.red.length + result.yellow.length + result.green.length
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Job Description Red Flag Detector"
        description="Paste any job description and instantly detect red flags, yellow flags, and green flags. Know what you're getting into before you apply."
        category="Job Search"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-8">
          <label
            htmlFor="job-description"
            className="block text-lg font-bold text-gray-900 mb-3"
          >
            Paste the Job Description
          </label>
          <p className="text-sm text-gray-500 mb-4">
            Copy and paste the full job listing below. The more text you include,
            the more flags we can detect.
          </p>
          <textarea
            id="job-description"
            rows={15}
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            placeholder="Paste the full job description here..."
            className="w-full border border-gray-300 rounded-xl p-4 text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent resize-y text-sm leading-relaxed"
          />

          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleDetect}
              disabled={!jobDescription.trim()}
              className="bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold py-3 px-8 rounded-xl shadow-lg hover:from-red-600 hover:to-orange-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Detect Red Flags
            </button>
            {hasAnalyzed && (
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Start Over
              </button>
            )}
          </div>
        </div>

        {/* Results */}
        {hasAnalyzed && result && (
          <div className="space-y-8">
            {/* Summary Bar */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                Analysis Summary
              </h2>
              {totalFlags === 0 ? (
                <p className="text-gray-600">
                  No flags were detected in this job description. This could mean
                  the listing is straightforward, or the language used does not
                  match our pattern library. Use your best judgment during the
                  interview process.
                </p>
              ) : (
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    <span className="text-red-800 font-bold text-lg">
                      {result.red.length}
                    </span>
                    <span className="text-red-700 text-sm font-medium">
                      red flag{result.red.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                    <AlertCircle className="w-5 h-5 text-amber-600" />
                    <span className="text-amber-800 font-bold text-lg">
                      {result.yellow.length}
                    </span>
                    <span className="text-amber-700 text-sm font-medium">
                      yellow flag{result.yellow.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="text-green-800 font-bold text-lg">
                      {result.green.length}
                    </span>
                    <span className="text-green-700 text-sm font-medium">
                      green flag{result.green.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Red Flags Section */}
            <div>
              <h3 className="text-xl font-bold text-red-700 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Red Flags
              </h3>
              {result.red.length > 0 ? (
                result.red.map((detected, i) => (
                  <FlagCard key={i} detected={detected} variant="red" />
                ))
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-green-800 font-medium">
                    None detected - no red flags found in this job description.
                  </span>
                </div>
              )}
            </div>

            {/* Yellow Flags Section */}
            <div>
              <h3 className="text-xl font-bold text-amber-700 mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                Yellow Flags
              </h3>
              {result.yellow.length > 0 ? (
                result.yellow.map((detected, i) => (
                  <FlagCard key={i} detected={detected} variant="yellow" />
                ))
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                  <span className="text-green-800 font-medium">
                    None detected - no yellow flags found in this job description.
                  </span>
                </div>
              )}
            </div>

            {/* Green Flags Section */}
            <div>
              <h3 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                Green Flags
              </h3>
              {result.green.length > 0 ? (
                result.green.map((detected, i) => (
                  <FlagCard key={i} detected={detected} variant="green" />
                ))
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-3 flex items-center gap-2">
                  <span className="text-gray-500">--</span>
                  <span className="text-gray-600 font-medium">
                    None detected - no green flags found. This does not
                    necessarily mean it is a bad listing, but the employer may not
                    have highlighted their benefits.
                  </span>
                </div>
              )}
            </div>

            {/* CTA */}
            <ToolCTA
              headline="Found a good one? Apply with a perfectly tailored resume."
              description="Our AI resume builder analyzes job descriptions and tailors every bullet point to match what the employer is looking for."
              buttonText="Create My Tailored Resume"
            />
          </div>
        )}

        {/* Educational Content */}
        <section className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mt-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">How to Spot Red Flags in Job Postings</h3>
          <div className="space-y-4 text-gray-700 leading-relaxed">
            <p>
              Not every job posting is what it seems. While most employers write listings in good faith, some use language patterns that signal deeper problems — high turnover, unrealistic expectations, or poor workplace culture. Learning to identify these patterns before you apply can save you months of frustration in the wrong role.
            </p>
            <p>
              <strong>Vague compensation language</strong> is one of the most common warning signs. Phrases like &quot;competitive salary&quot; or &quot;compensation commensurate with experience&quot; without a specific range often indicate below-market pay. The{' '}
              <a href="https://www.shrm.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Society for Human Resource Management (SHRM)</a>{' '}
              notes that pay transparency laws in many states now require salary ranges in job postings, so the absence of one in jurisdictions where it is not yet required may be worth questioning.
            </p>
            <p>
              <strong>Hyperbolic language</strong> like &quot;rockstar,&quot; &quot;ninja,&quot; or &quot;we are a family&quot; can mask boundary issues. According to career researchers, these phrases frequently correlate with environments where employees are expected to work beyond their job scope without additional compensation. Similarly, an excessively long list of required skills — sometimes 20 or more technologies — can signal that the employer does not fully understand the role or expects one person to fill multiple positions.
            </p>
            <p>
              On the positive side, look for <strong>green flags</strong> such as specific salary ranges, defined career growth paths, mentions of mentorship programs, and concrete benefits. Resources like the{' '}
              <a href="https://www.glassdoor.com/blog/guide/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Glassdoor Career Guides</a>{' '}
              and the{' '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics</a>{' '}
              can help you benchmark whether a listing&apos;s requirements and offerings are in line with industry norms.
            </p>
            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://www.shrm.org/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">SHRM — Society for Human Resource Management</a>
              {' · '}
              <a href="https://www.glassdoor.com/blog/guide/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Glassdoor Career Guides</a>
              {' · '}
              <a href="https://www.indeed.com/career-advice/finding-a-job/red-flags-in-job-postings" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: Red Flags in Job Postings</a>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
